<template>
  <div>
    <div class="title">{{ $t('common.imageSelector.title') }}</div>
    <div class="image-selector">
      <!-- プレビューエリア -->
      <div class="preview-cell">
        <!-- <image-view class="preview" v-if="image && type === 'preset'" :src="image"/> -->
        <image-view v-if="type !== 'local'" class="preview" :src="displayingImage" :placeholder="placeholder" />
        <!-- <clipper
        v-show="type === 'local'"
        :class="type"
        class="preview"
        :src="imageData" :ratio="ratio" :mode="'switch'" ref="clipper"></clipper> -->
        <canvas
          v-show="type === 'local'"
          @mousedown="mouseDownEvent"
          @mouseup="mouseUpEvent"
          @mousemove="mouseMoveEvent"
          data-clip=""
          width="1000"
          height="1000"
          :class="type"
          ref="localImgCanvas"
        />
      </div>

      <!-- ボタン -->
      <div class="button-local">
        <local-image-selector class="button l" @update:file="loadLocalImage" prefix="" />
      </div>
      <div class="button-preset">
        <button class="button r" @click="openPresetDialog">{{ $t('common.imageSelector.selectFromPreset') }}</button>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import ImageView from "@/components/atoms/ImageView.vue";
import LocalImageSelector from "@/components/atoms/LocalImageSelector.vue";
import { openPresetSelectorDialog } from "@/lib/dialog";

import { setCanvas } from "@/lib/image-save";

function getMousePos(canvas, evt) {
  const rect = canvas.getBoundingClientRect(); // abs. size of element
  const scaleX = canvas.width / rect.width; // relationship bitmap vs. element for X
  const scaleY = canvas.height / rect.height; // relationship bitmap vs. element for Y

  return {
    x: (evt.clientX - rect.left) * scaleX, // scale mouse coordinates after they have
    y: (evt.clientY - rect.top) * scaleY, // been adjusted to be relative to element,
    dx: evt.movementX * scaleX,
    dy: evt.movementY * scaleY,
  };
}

function getHandlePoint(e) {
  const { x, y } = getMousePos(e.srcElement, e);
  const [l, t, r, b] = e.srcElement.dataset.clip.split(",").map((n) => parseInt(n, 10));
  const range = { l, t, r, b };

  const hp: any = {};

  hp.x = (r + l) / 2 > x ? "l" : "r";
  hp.ox = (r + l) / 2 > x ? "r" : "l";
  hp.dx = (r + l) / 2 > x ? -1 : 1;
  hp.y = (b + t) / 2 > y ? "t" : "b";
  hp.oy = (b + t) / 2 > y ? "b" : "t";
  hp.dy = (b + t) / 2 > y ? -1 : 1;

  if ((range[hp.x] - x) * (range[hp.x] - x) + (range[hp.y] - y) * (range[hp.y] - y) < 50 * 50) {
    return hp;
  }
  return { dragMode: true };
}

export default Vue.extend({
  components: {
    ImageView,
    LocalImageSelector,
  },
  props: {
    defaultImage: String,
    placeholder: String,
    value: String,
    ratio: {
      default: 1,
      type: Number,
    },
  },
  data() {
    return {
      imageData: null,
      image: null,
      type: null as null | "local" | "preset",

      handlePoint: null as any,
      imageArea: null,
    };
  },
  mounted() {
    const cv = this.$refs.localImgCanvas;
    setCanvas(cv);
  },

  computed: {
    displayingImage() {
      const { defaultImage, image, type } = this;

      return (type && image) || defaultImage;
    },
  },

  methods: {
    updateClip(canvas: any, range: any) {
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "rgb(255, 255, 255)";
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const { sx, sy, sw, sh } = this.imageArea;
      ctx.drawImage(this.image, sx, sy, sw, sh);
      ctx.beginPath();
      const T = 10;
      ctx.lineWidth = T;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
      ctx.strokeRect(range.l - T, range.t - T, range.r - range.l + T * 2, range.b - range.t + T * 2);

      ctx.restore();
      // eslint-disable-next-line no-param-reassign
      canvas.dataset.clip = [range.l, range.t, range.r, range.b].join(",");
    },
    mouseDownEvent(e) {
      this.handlePoint = getHandlePoint(e);
    },
    mouseUpEvent(e) {
      this.handlePoint = null;
    },
    mouseMoveEvent(e) {
      const hp = getHandlePoint(e);
      let cursor = "move";
      if (hp.x === "l" && hp.y === "t") cursor = "nw-resize";
      if (hp.x === "l" && hp.y === "b") cursor = "sw-resize";
      if (hp.x === "r" && hp.y === "t") cursor = "ne-resize";
      if (hp.x === "r" && hp.y === "b") cursor = "se-resize";
      e.srcElement.style.cursor = cursor;

      const { handlePoint } = this;
      if (!handlePoint) {
        return;
      }
      const canvas = e.srcElement as HTMLCanvasElement;
      const { y, dx, dy } = getMousePos(canvas, e);
      const [l, t, r, b] = e.srcElement.dataset.clip.split(",").map((n) => parseInt(n, 10));
      const range = { l, t, r, b };

      if (handlePoint.dragMode) {
        range.l += dx;
        range.r += dx;
        range.t += dy;
        range.b += dy;
      } else {
        range[handlePoint.y] = y;
        range[handlePoint.x] = range[handlePoint.ox] + (handlePoint.dx * (range.b - range.t)) / this.ratio;
      }
      this.updateClip(canvas, range);
    },
    loadLocalImage({ data }) {
      const img = new Image();

      img.onload = () => {
        const canvas = this.$refs.localImgCanvas as HTMLCanvasElement;
        const cw = canvas.width;
        const ch = canvas.height;
        let { width, height } = img;

        const MAX = cw - 200;
        if (width >= height && width > MAX) {
          height = (height * MAX) / width;
          width = MAX;
        } else if (height >= width && height > MAX) {
          width = (width * MAX) / height;
          height = MAX;
        }

        const ctx = canvas.getContext("2d")!;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let l = cw / 2 - width / 2;
        let t = ch / 2 - height / 2;
        this.imageArea = { sx: l, sy: t, sw: width, sh: height };
        this.image = img;

        let w;
        let h;
        if (width / height >= this.ratio) {
          w = width;
          h = width * this.ratio;
        } else {
          w = height / this.ratio;
          h = height;
        }
        l = cw / 2 - w / 2;
        t = ch / 2 - h / 2;
        this.updateClip(canvas, { l, t, r: l + w, b: t + h });

        this.type = "local";
        this.$emit("input", `file:${this.storagePath}`);
        this.$emit("local-image-change");
      };
      img.src = data;
    },
    async openPresetDialog() {
      const response = await openPresetSelectorDialog();

      if (!response) {
        return;
      }

      this.type = "preset";
      this.image = `preset:${response.key}`;
      this.$emit("input", `preset:${response.key}`);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.title {
  margin: 30px;
  margin-bottom: 0px;
  text-align: left;
  color: var(--color-text-normal);

  @include mq(sp) {
    margin: 15px;
    margin-bottom: 0px;
  }
}
.image-selector {
  width: 300px;
  height: 300px;
  margin: 0 30px;
  border: var(--color-border) 1px solid;

  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 50px;

  @include mq(sp) {
    width: calc(100% - 30px);
    margin: 0 15px;
    margin-bottom: 15px;
  }
}

.button-local {
  grid-row: 2;
  grid-column: 1;
}

.button-preset {
  grid-row: 2;
  grid-column: 2;
}

.preview-cell {
  grid-row: 1;
  grid-column: 1 / 3;
  background-color: var(--color-surface-muted);
  height: 250px;
  position: relative;
}

.preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: transparent;
}

canvas {
  max-width: 100%;
  max-height: 100%;
  margin: auto;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: var(--color-surface-muted);
}

.button {
  position: relative;
  color: var(--color-text-normal);
  background-color: var(--color-surface-subtle);
  border: none;
  padding: 0;
  margin: 0;
  width: calc(100% - 1px);
  height: calc(100% - 1px);
  display: inline-block;
  border-radius: 0;

  @include hover-overlay;
}
</style>
